import type {FeedItem} from 'iq-companies/types';

export type FeedDayGroup = {date: string; label: string; items: FeedItem[]};

// Ported from frontend/src/components/dashboard/home/feed_section.tsx (groupByDay/
// formatDayLabel/getFeedDate/itemKey) — same day-grouping convention as the logged-in
// dashboard feed, adapted to this app's FeedItem union. Not imported directly:
// frontend-monorepo and the legacy frontend app are separate Next.js builds with no shared
// package (see plan § Context, item 2).
export function getFeedItemDate(item: FeedItem): string {
  switch (item.type) {
    case 'experience':
      return item.interview_date;
    case 'recency':
      return item.recently_used_at;
    case 'question':
      return item.created_at;
  }
}

// Questions don't carry a unique `id`, only `question_id` — mirrors the dashboard's own
// `q-${question_id}` key convention for that case.
export function getFeedItemKey(item: FeedItem): string {
  return item.type === 'question' ? `q-${item.question_id}` : item.id;
}

export function formatDayLabel(dateStr: string): string {
  if (!dateStr) return 'Unknown date';
  const today = new Date().toISOString().slice(0, 10);
  const yesterday = new Date(Date.now() - 86_400_000).toISOString().slice(0, 10);
  if (dateStr === today) return 'Today';
  if (dateStr === yesterday) return 'Yesterday';
  const d = new Date(`${dateStr}T12:00:00Z`);
  const isCurrentYear = d.getUTCFullYear() === new Date().getUTCFullYear();
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(isCurrentYear ? {} : {year: 'numeric'}),
  });
}

export function groupByDay(items: FeedItem[]): FeedDayGroup[] {
  const map = new Map<string, FeedDayGroup>();
  for (const item of items) {
    const date = getFeedItemDate(item).slice(0, 10);
    if (!map.has(date)) map.set(date, {date, label: formatDayLabel(date), items: []});
    map.get(date)!.items.push(item);
  }
  return Array.from(map.values());
}
